import {
    GripVertical, X, CalendarRange
} from "lucide-react";
import { ReactSortable } from "react-sortablejs";
import Input from "./Input";
import { useUpload } from "../Use-auth";
import { useState } from "react";

const TaskDetails = ({ editData, setEditData, user, TaskEmployees, employees }) => {
    const { mutateAsync: uploadFile, isLoading } = useUpload();
    const [fullscreenImage, setFullscreenImage] = useState(null);
    const isEmployee = ["Development", "Design", "Sales"].includes(user?.department);

    if (!Array.isArray(editData?.description)) return null;

    const handleFileChange = async (files, index) => {
        const uploadedFiles = [];

        for (const file of files) {
            try {
                const data = await uploadFile(file);
                if (data.success) {
                    uploadedFiles.push({ name: file.name, url: data.url, type: file.type });
                }
            } catch (err) {
                console.error("Upload error:", err);
            }
        }

        setEditData((prev) => {
            const updated = [...prev.description];
            updated[index] = {
                ...updated[index],
                files: [...(updated[index].files || []), ...uploadedFiles],
            };
            return { ...prev, description: updated };
        });
    };

    const insertImageAtCursor = (url) => {
        const img = document.createElement("img");
        img.src = url;
        img.style.maxWidth = "100%";
        img.style.borderRadius = "6px";
        img.style.display = "block";
        img.style.margin = "6px 0";

        const selection = window.getSelection();
        if (!selection || !selection.rangeCount) return;

        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(img);

        range.setStartAfter(img);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
    };

    /* ---------- HANDLE IMAGE PASTE ---------- */
    const handlePaste = async (e) => {
        if (isEmployee) return;

        const items = e.clipboardData.items;
        for (const item of items) {
            if (item.type.startsWith("image")) {
                e.preventDefault();

                const file = item.getAsFile();
                try {
                    const res = await uploadFile(file);
                    insertImageAtCursor(res.url);
                } catch (err) {
                    console.error("Image upload failed", err);
                }
            }
        }
    };

    const parseTaskAssign = (value) => {
        try {
            if (!value) return [];

            if (Array.isArray(value)) return value;

            if (typeof value === "string") {
                let parsed = JSON.parse(value);

                if (typeof parsed === "string") {
                    return parsed.split(",");
                }

                if (Array.isArray(parsed)) {
                    return parsed;
                }
            }

            return [];
        } catch (e) {
            console.warn("Invalid taskAssign:", value);
            return [];
        }
    };

    return (
        <div className="w-full mx-auto mt-10">
            <h3 className="text-[14px] md:text-[14px] font-semibold text-gray-700 mb-4 border-b pb-2">
                Task Details
            </h3>
            <div className="space-y-6 min-h-[600px] overflow-y-auto pr-2 scrollbar-none">
                {editData.description.map((desc, index) => (
                    <div
                        key={index}
                        className="bg-white shadow-sm border border-gray-200 rounded-lg p-2 sm:p-5 space-y-5"
                    >
                        <div>
                            <label className="text-gray-600 font-medium block mb-1 text-[12px] md:text-[14px]">
                                Store Link
                            </label>
                            <Input
                                type="text"
                                className="w-full border text-green-600 border-gray-300 rounded-lg px-3 py-2 text-[12px] md:text-[14px] focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition"
                                style={{ color: "#16a34a" }}
                                value={desc.storeLink || ""}
                                onChange={(e) => {
                                    if (isEmployee) return;
                                    setEditData((prev) => {
                                        const updated = [...prev.description];
                                        updated[index] = {
                                            ...updated[index],
                                            storeLink: e.target.value,
                                        };
                                        return { ...prev, description: updated };
                                    });
                                }}
                            />
                            {desc.storeLink && (
                                <a
                                    href={desc.storeLink.startsWith("http") ? desc.storeLink : `https://${desc.storeLink}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 underline text-[12px] md:text-[14px] mt-1 inline-block"
                                >
                                    Open Store Link
                                </a>
                            )}
                        </div>

                        {/* Reference Link */}
                        {!(isEmployee && desc.referenceLinkDisabled) && (
                            <div>
                                <label className="flex items-center text-gray-600 text-[12px] md:text-[14px] font-medium mb-1">
                                    Reference Link
                                </label>

                                <div className="flex items-start">
                                    {!isEmployee && (
                                        <input
                                            type="checkbox"
                                            className="mr-2 w-4 h-4"
                                            style={{ color: "#16a34a" }}
                                            checked={!!desc.referenceLinkDisabled}
                                            onChange={(e) =>
                                                setEditData((prev) => {
                                                    const updated = [...prev.description];
                                                    updated[index] = {
                                                        ...updated[index],
                                                        referenceLinkDisabled: e.target.checked,
                                                    };
                                                    return { ...prev, description: updated };
                                                })
                                            }
                                        />
                                    )}

                                    <div className="w-full">
                                        <Input
                                            type="text"
                                            className={`w-full border rounded-lg px-3 py-2 text-[12px] md:text-[14px] transition focus:outline-none ${desc.referenceLinkDisabled
                                                ? "bg-gray-100 cursor-not-allowed opacity-50"
                                                : "focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                                                }`}
                                            value={desc.referenceLink || ""}
                                            style={{ color: "#16a34a" }}
                                            onChange={(e) => {
                                                if (isEmployee || desc.referenceLinkDisabled) return;
                                                setEditData((prev) => {
                                                    const updated = [...prev.description];
                                                    updated[index] = {
                                                        ...updated[index],
                                                        referenceLink: e.target.value,
                                                    };
                                                    return { ...prev, description: updated };
                                                });
                                            }}
                                        />
                                        {desc.referenceLink && (
                                            <a
                                                href={desc.referenceLink.startsWith("http") ? desc.referenceLink : `https://${desc.referenceLink}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 underline text-[12px] md:text-[14px] mt-1 inline-block"
                                            >
                                                Open Reference Link
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Figma Link */}
                        {!(isEmployee && desc.figmaLinkDisabled) && (
                            <div>
                                <label className="flex items-center text-gray-600 text-[12px] md:text-[14px] font-medium mb-1">
                                    Figma Link
                                </label>
                                <div className="flex items-start">
                                    {!isEmployee && (
                                        <input
                                            type="checkbox"
                                            className="mr-2 w-4 h-4"
                                            style={{ color: "#16a34a" }}
                                            checked={!!desc.figmaLinkDisabled}
                                            onChange={(e) =>
                                                setEditData((prev) => {
                                                    const updated = [...prev.description];
                                                    updated[index].figmaLinkDisabled = e.target.checked;
                                                    return { ...prev, description: updated };
                                                })
                                            }
                                        />
                                    )}
                                    <div className="w-full">
                                        <Input
                                            type="text"
                                            className={`w-full border rounded-lg px-3 py-2 text-[12px] md:text-[14px] transition focus:outline-none ${desc.figmaLinkDisabled
                                                ? "bg-gray-100 cursor-not-allowed"
                                                : "focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                                                }`}
                                            value={desc.figmaLink || ""}
                                            style={{ color: "#16a34a" }}
                                            onChange={(e) => {
                                                if (isEmployee || desc.figmaLinkDisabled) return;
                                                setEditData((prev) => {
                                                    const updated = [...prev.description];
                                                    updated[index] = {
                                                        ...updated[index],
                                                        figmaLink: e.target.value,
                                                    };
                                                    return { ...prev, description: updated };
                                                });
                                            }}
                                        />

                                        {desc.figmaLink && (
                                            <a
                                                href={desc.figmaLink.startsWith("http") ? desc.figmaLink : `https://${desc.figmaLink}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 underline text-[12px] md:text-[14px] mt-1 inline-block"
                                            >
                                                Open Figma Link
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>)}
                        <div>
                            <label className="text-gray-600 text-[12px] md:text-[14px] font-medium block mb-1">
                                Task Description
                            </label>
                            <div
                                contentEditable={!isEmployee}
                                suppressContentEditableWarning
                                className="w-full border rounded-lg px-3 py-2 text-[12px] md:text-[14px] focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition min-h-[80px]" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                                onPaste={handlePaste}
                                onClick={(e) => e.currentTarget.focus()}
                                onBlur={(e) => {
                                    if (isEmployee) return;

                                    const div = e.currentTarget;

                                    let html = div.innerHTML; 

                                    setEditData((prev) => {
                                        const updated = [...prev.description];
                                        updated[index] = {
                                            ...updated[index],
                                            subdescription: html, 
                                        };
                                        return { ...prev, description: updated };
                                    });
                                }}
                                dangerouslySetInnerHTML={{ __html: desc.subdescription || "" }}
                            />
                        </div>

                        <div className="border  border-gray-300 p-3 rounded-sm">
                            <label className="text-gray-600 text-[12px] md:text-[14px] font-medium block mb-2">
                                Sub Tasks
                            </label>
                            <ReactSortable
                                list={desc.taskdescription || []}
                                setList={(newList) =>
                                    !isEmployee &&
                                    setEditData((prev) => {
                                        const updated = [...prev.description];

                                        updated[index] = {
                                            ...updated[index],
                                            taskdescription: newList.map((item) => ({ ...item })),
                                        };

                                        return { ...prev, description: updated };
                                    })
                                }
                                handle={isEmployee ? null : ".handle"}
                                disabled={isEmployee}
                            >
                                {(desc.taskdescription || []).map((item, i) => (
                                    <div key={item.id || item._id || i} className="flex flex-col md:flex-row items-start gap-2 mb-3 md:items-start">
                                        {!isEmployee && <GripVertical size={18} className="handle cursor-grab mt-2" />}
                                        <div
                                            contentEditable={!isEmployee}
                                            suppressContentEditableWarning
                                            className="w-full md:flex-1 md:max-w-[600px] border border-gray-300 rounded-lg px-3 py-2 text-[12px] md:text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-400 min-h-[80px] break-words whitespace-pre-wrap" onPaste={handlePaste}
                                            onBlur={(e) => {
                                                const html = e.currentTarget.innerHTML;

                                                setEditData((prev) => {
                                                    const updated = [...prev.description];
                                                    const taskDesc = [...updated[index].taskdescription];

                                                    taskDesc[i] = {
                                                        ...(typeof taskDesc[i] === "string"
                                                            ? { description: taskDesc[i] }
                                                            : taskDesc[i]),
                                                        description: html,
                                                    };

                                                    updated[index] = { ...updated[index], taskdescription: taskDesc };

                                                    return { ...prev, description: updated };
                                                });
                                            }}
                                            dangerouslySetInnerHTML={{ __html: item.description || "" }}
                                        />

                                        {!isEmployee && (
                                            <button
                                                type="button"
                                                className="p-[5px] text-red-600 font-medium rounded hover:bg-red-100"
                                                onClick={() => {
                                                    setEditData((prev) => {
                                                        const updated = [...prev.description];
                                                        updated[index].taskdescription = updated[index].taskdescription.filter((_, idx) => idx !== i);
                                                        return { ...prev, description: updated };
                                                    });
                                                }}
                                            >
                                                <X size={18} />
                                            </button>
                                        )}

                                        {/* Task Assignment */}
                                        <div className="flex flex-col zs:flex-row gap-2 mb-3">
                                            <TaskEmployees
                                                selected={parseTaskAssign(item.taskAssign)}
                                                onChange={(arr) =>
                                                    setEditData((prev) => {
                                                        const updated = [...prev.description];
                                                        const taskDesc = [...updated[index].taskdescription];
                                                        taskDesc[i] = { ...(typeof taskDesc[i] === "string" ? { description: taskDesc[i] } : taskDesc[i]), taskAssign: arr };
                                                        updated[index] = { ...updated[index], taskdescription: taskDesc };
                                                        return { ...prev, description: updated };
                                                    })
                                                }
                                                employees={employees}
                                                placement="top"
                                                align={window.innerWidth <= 768 ? "left" : "right"}
                                                hideSelected={isEmployee}
                                                showDropdown={!isEmployee}
                                            />

                                            <div>
                                                <input
                                                    type="date"
                                                    className="border rounded-lg px-2 py-1 text-[12px] md:text-[14px]"
                                                    value={item.taskDate || ""}
                                                    disabled={isEmployee}
                                                    onChange={(e) =>
                                                        setEditData((prev) => {
                                                            const updated = [...prev.description];
                                                            const taskDesc = [...updated[index].taskdescription];

                                                            taskDesc[i] = {
                                                                ...(typeof taskDesc[i] === "string"
                                                                    ? { description: taskDesc[i] }
                                                                    : taskDesc[i]),
                                                                taskDate: e.target.value,
                                                            };

                                                            updated[index] = { ...updated[index], taskdescription: taskDesc };

                                                            return { ...prev, description: updated };
                                                        })
                                                    }
                                                />
                                            </div>

                                            <div>
                                                <select
                                                    className="border rounded-lg px-2 py-1 text-[12px] md:text-[14px]"
                                                    value={item.taskStatus || ""}
                                                    disabled={isEmployee}
                                                    onChange={(e) =>
                                                        setEditData((prev) => {
                                                            const updated = [...prev.description];
                                                            const taskDesc = [...updated[index].taskdescription];
                                                            taskDesc[i] = { ...(typeof taskDesc[i] === "string" ? { description: taskDesc[i] } : taskDesc[i]), taskStatus: e.target.value };
                                                            updated[index] = { ...updated[index], taskdescription: taskDesc };
                                                            return { ...prev, description: updated };
                                                        })
                                                    }
                                                >
                                                    <option value="">Select Status</option>
                                                    <option value="Pending">Pending</option>
                                                    <option value="Progress">Progress</option>
                                                    <option value="Testing">Testing</option>
                                                    <option value="Complete">Complete</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </ReactSortable>

                            {!isEmployee && (
                                <button
                                    type="button"
                                    className="mt-2 text-blue-600 hover:underline text-[12px] md:text-[14px] font-medium"
                                    onClick={() =>
                                        setEditData((prev) => {
                                            const updated = [...prev.description];
                                            updated[index] = {
                                                ...updated[index],
                                                taskdescription: [
                                                    ...(updated[index].taskdescription || []),
                                                    { description: "", taskAssign: [], taskDate: "", taskStatus: "" },
                                                ],
                                            };
                                            return { ...prev, description: updated };
                                        })
                                    }
                                >
                                    + Sub Tasks
                                </button>
                            )}
                        </div>

                        <div>
                            {!isEmployee && (
                                <>
                                    <label className="text-gray-600 text-[12px] md:text-[14px] font-medium block mb-1">
                                        Upload Files
                                    </label>
                                    <input
                                        type="file"
                                        multiple
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-[12px] md:text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition"
                                        onChange={(e) => handleFileChange(e.target.files, index)}
                                    />
                                </>)}
                            <div>
                                <label className="text-gray-600 text-[12px] md:text-[14px] mt-[20px] font-medium block mb-2">
                                    Images
                                </label>
                                <div className="flex items-center flex-wrap">
                                    {(desc.files || []).map((file, i) => {
                                        const src = file.url ? file.url : URL.createObjectURL(file);

                                        if (file.type?.startsWith("image/")) {
                                            return (
                                                <div
                                                    key={i}
                                                    className="relative w-[200px] h-[200px] m-[10px]"
                                                >
                                                    <img
                                                        src={src}
                                                        alt={file.name}
                                                        className="w-full h-full object-cover rounded border cursor-pointer"
                                                        onClick={() => setFullscreenImage(src)}
                                                    />
                                                    {!isEmployee && (
                                                        <button
                                                            type="button"
                                                            className="absolute top-1 right-1 bg-black/60 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setEditData((prev) => {
                                                                    const updated = [...prev.description];
                                                                    updated[index].files = updated[index].files.filter(
                                                                        (_, idx) => idx !== i
                                                                    );
                                                                    return { ...prev, description: updated };
                                                                });
                                                            }}
                                                        >
                                                            <X size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        }

                                        return (
                                            <div
                                                key={i}
                                                className="w-20 h-20 flex items-center justify-center border rounded text-xs text-gray-600 text-center p-1 m-[10px]"
                                            >
                                                {file.name}
                                            </div>
                                        );
                                    })}
                                </div>

                            </div>
                            {fullscreenImage && (
                                <div
                                    className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50"
                                    onClick={() => setFullscreenImage(null)}
                                >
                                    <div className="relative">
                                        <img
                                            src={fullscreenImage}
                                            alt="Fullscreen"
                                            className="max-h-[90vh] max-w-[90vw] object-contain rounded"
                                        />
                                        <button
                                            onClick={() => setFullscreenImage(null)}
                                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold"
                                        >
                                            <X size={18} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TaskDetails;
